
import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
  DeleteIconWrapper
} from './styles';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import DocumentTypeModal from './components/DocumentTypeModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { searchDocumentTypes, deleteDocumentType } from '../../../modules/masterdata/actions';
import {
  selectDocumentTypesList,
  selectSearchDocumentTypesCount,
  selectCreateDocumentTypeSuccess,
  selectCreateDocumentTypeError,
  selectUpdateDocumentTypeSuccess,
  selectUpdateDocumentTypeError,
  selectDeleteDocumentTypeSuccess,
  selectDeleteDocumentTypeError
} from '../../../modules/masterdata/selectors';
import {
  resetCreateDocumentTypeState,
  resetUpdateDocumentTypeState,
  resetDeleteDocumentTypeState
} from '../../../modules/masterdata/reducers';
import { IDocumentType } from '../../../modules/masterdata/types';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../components/common/AlertPopup';

const DocumentTypes: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const documentTypes = useSelector(selectDocumentTypesList);
  const totalCount = useSelector(selectSearchDocumentTypesCount);

  // Success/Error Selectors
  const createSuccess = useSelector(selectCreateDocumentTypeSuccess);
  const createError = useSelector(selectCreateDocumentTypeError);
  const updateSuccess = useSelector(selectUpdateDocumentTypeSuccess);
  const updateError = useSelector(selectUpdateDocumentTypeError);
  const deleteSuccess = useSelector(selectDeleteDocumentTypeSuccess);
  const deleteError = useSelector(selectDeleteDocumentTypeError);

  const PAGE_SIZE = 10;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<IDocumentType | null>(null);

  // Confirmation Dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('document-types');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Document Types', path: '/master-data/document-types' },
    ]);
    setIsShow(true);
    dispatch(searchDocumentTypes({ search: '', page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);

  const [page, setPage] = useState(1);

  // Handle Notifications and Resets
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Document Type created successfully', severity: 'success' });
      dispatch(searchDocumentTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetCreateDocumentTypeState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetCreateDocumentTypeState());
    }

    if (updateSuccess) {
      setAlert({ message: 'Document Type updated successfully', severity: 'success' });
      dispatch(searchDocumentTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetUpdateDocumentTypeState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateDocumentTypeState());
    }

    if (deleteSuccess) {
      setAlert({ message: 'Document Type deleted successfully', severity: 'success' });
      dispatch(searchDocumentTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetDeleteDocumentTypeState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteDocumentTypeState());
    }
  }, [createSuccess, createError, updateSuccess, updateError, deleteSuccess, deleteError, dispatch, page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    dispatch(searchDocumentTypes({ search: '', page: newPage, page_size: PAGE_SIZE }));
  };

  const handleCreateClick = () => {
    setSelectedDocType(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (docType: IDocumentType) => {
    setSelectedDocType(docType);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteDocumentType(deleteId));
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Document Types</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Document Type
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Req Issue Date</StyledTableCell>
                <StyledTableCell>Req Expiry Date</StyledTableCell>
                <StyledTableCell>Is Mandatory</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {documentTypes && documentTypes.length > 0 ? (
                documentTypes.map((docType: IDocumentType, index: number) => (
                  <StyledTableRow key={docType.document_id}>
                    <StyledTableCell>{docType.document_id}</StyledTableCell>
                    <StyledTableCell>{docType.document_type}</StyledTableCell>
                    <StyledTableCell>{docType.document_description}</StyledTableCell>
                    <StyledTableCell>{docType.req_issue_date ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{docType.req_expiry_date ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{docType.is_mandatory ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(docType)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                        <DeleteIconWrapper onClick={() => handleDeleteClick(docType.document_id)}>
                          <DeleteIcon fontSize="small" />
                        </DeleteIconWrapper>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={7} align="center">No document types found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <DocumentTypeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        documentType={selectedDocType}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document Type"
        message="Are you sure you want to delete this document type?"
      />
    </Box>
  );
};

export default DocumentTypes;
